import { BookUser, Target, Settings, LogOut, GitCompare, Building2, Check, Plus, Star, ClipboardList, ChevronDown, Briefcase, GraduationCap, FolderKanban, UsersRound, ShieldCheck } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { NavLink } from "@/components/NavLink";

const menuSections = [
  {
    title: "Assessment",
    items: [
      { id: 'candidates', title: "Candidates", url: "/dashboard", icon: Target },
    ]
  },
  {
    title: "Analysis",
    items: [
      { id: 'profiles', title: "Ideal Profiles", url: "/ideal", icon: Star },
      { id: 'talent', title: "Talent Optimization", url: "/talent", icon: GitCompare },
      { id: 'positions', title: "Positions", url: "/positions", icon: FolderKanban },
      { id: 'work', title: "Work Behavior", url: "/work", icon: Briefcase },
      { id: 'management', title: "Management", url: "/management", icon: ClipboardList },
      { id: 'org', title: "Org Chart", url: "/org", icon: Building2 },
    ]
  },
  {
    title: "Resources",
    items: [
      { id: 'templates', title: "Standard Profiles", url: "/templates", icon: BookUser },
      { id: 'training', title: "Training", url: "/training", icon: GraduationCap },
      { id: 'team', title: "Team", url: "/team", icon: UsersRound },
    ]
  },
  {
    title: "Account",
    items: [
      { id: 'settings', title: "Settings", url: "/settings", icon: Settings },
    ]
  }
];

interface MinimalSidebarProps {
  onSignOut?: () => void;
}

export function MinimalSidebar({ onSignOut }: MinimalSidebarProps) {
  const { open } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const { currentCompany, availableCompanies, switchCompany, loading } = useCompany();

  const sections = menuSections.map(section => {
    if (section.title === 'Account' && isSuperAdmin()) {
      return {
        ...section,
        items: [
          ...section.items,
          { id: 'superadmin', title: 'Super Admin', url: '/superadmin', icon: ShieldCheck },
        ],
      };
    }
    return section;
  });

  return (
    <Sidebar collapsible="icon" className="border-none bg-sidebar">
      <SidebarHeader className="px-2 py-2">
        <div className="flex items-center justify-between gap-2">
          {open && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:bg-state-hover rounded-[5px] px-2 py-1.5 transition-colors duration-fast flex-1 min-w-0 group">
                  {currentCompany?.logo_url ? (
                    <img 
                      src={currentCompany.logo_url} 
                      alt={currentCompany.company_name}
                      className="h-5 w-5 rounded-[4px] object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="p-1 bg-foreground rounded-[4px] flex-shrink-0">
                      <Building2 className="h-3 w-3 text-background" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-[13px] font-medium text-foreground truncate">
                      {loading ? "Loading..." : currentCompany?.company_name || (availableCompanies.length === 1 ? availableCompanies[0].company_name : "Select Company")}
                    </div>
                  </div>
                  <ChevronDown className="h-3 w-3 text-muted-foreground flex-shrink-0 transition-transform group-data-[state=open]:rotate-180" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[220px] bg-card z-50" sideOffset={4}>
                {availableCompanies.length > 0 ? (
                  <>
                    {availableCompanies.map((company) => (
                      <DropdownMenuItem
                        key={company.company_id}
                        onClick={() => switchCompany(company.company_id)}
                        className="flex items-center gap-2 cursor-pointer py-1.5"
                      >
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        <div className="text-[13px] font-medium truncate flex-1 min-w-0">
                          {company.company_name}
                        </div>
                        {currentCompany?.company_id === company.company_id && (
                          <Check className="h-3.5 w-3.5 text-foreground" />
                        )}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                ) : (
                  <DropdownMenuItem disabled className="text-muted-foreground text-[11px]">
                    No companies available
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => navigate('/companies/new')}
                  className="flex items-center gap-2 cursor-pointer py-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span className="text-[13px] font-medium">Create New Company</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!open && (
            <div className="flex items-center justify-center w-full">
              {currentCompany?.logo_url ? (
                <img 
                  src={currentCompany.logo_url} 
                  alt={currentCompany.company_name}
                  className="h-5 w-5 rounded-[4px] object-cover"
                />
              ) : (
                <div className="p-1 bg-foreground rounded-[4px]">
                  <Building2 className="h-3 w-3 text-background" />
                </div>
              )}
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="py-0">
        {sections.map((section, index) => (
          <div key={section.title}>
            {index > 0 && <div className="my-1 mx-3 h-px bg-sidebar-border" />}
            
            <SidebarGroup>
              {open && (
                <SidebarGroupLabel className="px-3 mb-0 text-[11px] font-medium uppercase tracking-wider text-muted-foreground h-6">
                  {section.title}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu className="space-y-0 px-1">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.url;
                    return (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton asChild>
                          <NavLink
                            to={item.url}
                            className={`
                              group relative flex items-center gap-2 px-2 py-1 rounded-[4px] transition-colors duration-fast
                              ${!open ? "justify-center" : ""}
                              ${isActive 
                                ? "bg-state-active text-foreground font-medium" 
                                : "text-muted-foreground hover:bg-state-hover hover:text-foreground"
                              }
                            `}
                          >
                            <item.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? "text-accent" : ""} ${!open ? "h-4 w-4" : ""}`} />
                            {open && (
                              <span className="text-[13px] font-medium">
                                {item.title}
                              </span>
                            )}
                            {isActive && (
                              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-accent rounded-r" />
                            )}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border px-3 py-1.5 mt-auto">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <div className={`flex items-center ${!open ? 'justify-center' : 'justify-between'} gap-2 px-2 py-0.5`}>
                  {open && user && (
                    <div className="text-[11px] text-muted-foreground truncate flex-1">
                      {user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0]}
                    </div>
                  )}
                  {onSignOut && (
                    <button
                      onClick={onSignOut}
                      className="h-7 w-7 p-0 rounded-[4px] hover:bg-state-hover text-muted-foreground hover:text-foreground transition-colors duration-fast flex items-center justify-center"
                      title="Sign out"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
